import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Tabs } from "Components/Tabs/Tabs";
import { NotificationCard } from "Components/NotificationCard/NotificationCard";
import { DynamicIcon } from "Components/DynamicIcon/DynamicIcon";
import { InformativeText } from "Components/InformativeText/InformativeText";
import "./Notifications.scss";

/**
 * Componente de notificaciones que muestra las notificaciones del usuario organizadas en pestañas.
 * Muestra notificaciones recientes y antiguas con indicadores visuales para el estado no leído.
 *
 * @component
 * @returns {JSX.Element} La interfaz de notificaciones renderizada
 */
export const Notifications = () => {
  const [selectedTab, setSelectedTab] = useState(1);
  const { notificacionesUsuario } = useSelector(
    (state) => state.notificacionesUsuario
  );
  const tabItems = [{ label: "Más recientes" }, { label: "Antiguas" }];

  const calculateRelativeTime = (dateString) => {
    const now = new Date();
    const notificationDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - notificationDate) / (1000 * 60));

    if (diffInMinutes < 1) return "Ahora";
    if (diffInMinutes < 60)
      return `Hace ${diffInMinutes} minuto${diffInMinutes !== 1 ? "s" : ""}`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `Hace ${diffInHours} hora${diffInHours !== 1 ? "s" : ""}`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7)
      return `Hace ${diffInDays} día${diffInDays !== 1 ? "s" : ""}`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `Hace ${diffInWeeks} semana${diffInWeeks !== 1 ? "s" : ""}`;
  };

  const isRecentNotification = (notification) => {
    if (!notification.fechaEntrega) return true;

    const now = new Date();
    const notificationDate = new Date(notification.fechaEntrega);
    const diffInHours = Math.floor((now - notificationDate) / (1000 * 60 * 60));
    return diffInHours <= 24;
  };

  const recentNotifications = [...(notificacionesUsuario || [])]
    .filter((notification) => isRecentNotification(notification))
    .map((notification) => ({
      ...notification,
      icon: DynamicIcon,
      time: notification.fechaEntrega
        ? calculateRelativeTime(notification.fechaEntrega)
        : "Ahora",
      isRecent: true,
    }))
    .reverse();

  const oldNotifications = [...(notificacionesUsuario || [])]
    .filter((notification) => !isRecentNotification(notification))
    .map((notification) => ({
      ...notification,
      icon: DynamicIcon,
      time: calculateRelativeTime(notification.fechaEntrega),
      isRecent: false,
    }))
    .reverse();

  return (
    <div className="notifications">
      <div className="notifications__header">
        <Tabs
          items={tabItems}
          onChange={(index) => setSelectedTab(index)}
          className="tabs--full-width"
        />
        <h2 className="notifications__title">Notificaciones:</h2>
      </div>

      <div className="notifications__tab-content">
        {selectedTab === 1 && (
          <div className="notifications__list">
            {recentNotifications.length > 0 ? (
              recentNotifications.map((notification) => (
                <NotificationCard
                  key={notification.idNotificacionUsuario}
                  idNotificacionUsuario={notification.idNotificacionUsuario}
                  icon={notification.icon}
                  icono={notification.icono}
                  titulo={notification.titulo}
                  descripcion={notification.descripcion}
                  time={notification.time}
                  isRecent={notification.isRecent}
                />
              ))
            ) : (
              <div className="notifications__empty">
                <InformativeText>
                  No tienes notificaciones recientes
                </InformativeText>
              </div>
            )}
          </div>
        )}

        {selectedTab === 2 && (
          <div className="notifications__list">
            {oldNotifications.length > 0 ? (
              oldNotifications.map((notification) => (
                <NotificationCard
                  key={notification.idNotificacionUsuario}
                  idNotificacionUsuario={notification.idNotificacionUsuario}
                  icon={notification.icon}
                  icono={notification.icono}
                  titulo={notification.titulo}
                  descripcion={notification.descripcion}
                  time={notification.time}
                  isRecent={notification.isRecent}
                />
              ))
            ) : (
              <div className="notifications__empty">
                <InformativeText>
                  No tienes notificaciones antiguas
                </InformativeText>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
